import React from 'react';
import { Tab, Tabs, Typography, makeStyles } from '@material-ui/core';
import { getCommercialSectionNavigation } from './commercialNavigationState';

const useStyles = makeStyles(theme => ({
  root: {
    alignItems: 'flex-end',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    minWidth: 0,
    [theme.breakpoints.down('sm')]: { alignItems: 'stretch', flexDirection: 'column', gap: theme.spacing(1) },
  },
  copy: { flex: '0 1 460px', minWidth: 220, paddingBottom: theme.spacing(1.25), [theme.breakpoints.down('sm')]: { flexBasis: 'auto' } },
  label: { fontWeight: 600 },
  tabs: {
    flex: '0 1 auto',
    minWidth: 0,
    '& .MuiTab-root': { minHeight: 44, minWidth: 0, paddingLeft: theme.spacing(1.5), paddingRight: theme.spacing(1.5), textTransform: 'none' },
  },
  groupedRoot: { alignItems: 'stretch', flexDirection: 'column', gap: theme.spacing(1) },
  groupedCopy: { flex: '0 1 auto', maxWidth: 680, paddingBottom: 0 },
  groupGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    width: '100%',
  },
  group: {
    borderLeft: `2px solid ${theme.palette.divider}`,
    minWidth: 0,
    paddingLeft: theme.spacing(1),
  },
  groupLabel: { display: 'block', fontWeight: 600, paddingLeft: theme.spacing(1.5) },
  groupLinks: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' },
  groupLink: {
    alignItems: 'center',
    borderBottom: '2px solid transparent',
    color: theme.palette.text.secondary,
    display: 'flex',
    fontSize: theme.typography.pxToRem(14),
    justifyContent: 'center',
    minHeight: 40,
    padding: theme.spacing(.5, 1.5),
    textAlign: 'center',
    textDecoration: 'none',
    '&:hover': { background: theme.palette.action.hover, color: theme.palette.text.primary },
    '&:focus': { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: -2 },
  },
  activeGroupLink: { borderBottomColor: theme.palette.secondary.main, color: theme.palette.text.primary, fontWeight: 600 },
  guide: {
    background: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    display: 'grid',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
  },
  guideLabel: { display: 'block', fontWeight: 600 },
}));

export function CommercialTaskGuide({ title, when, changes, start }) {
  const classes = useStyles();
  if (!when || !changes || !start) return null;
  return <aside className={classes.guide} aria-label={`${title} workflow`}>
    <div>
      <Typography className={classes.guideLabel} variant="overline" color="textSecondary">Use this page when</Typography>
      <Typography variant="body2">{when}</Typography>
    </div>
    <div>
      <Typography className={classes.guideLabel} variant="overline" color="textSecondary">What changes</Typography>
      <Typography variant="body2">{changes}</Typography>
    </div>
    <div>
      <Typography className={classes.guideLabel} variant="overline" color="textSecondary">Start here</Typography>
      <Typography variant="body2">{start}</Typography>
    </div>
  </aside>;
}

export function CommercialSectionNavigation({ area, current }) {
  const classes = useStyles();
  const navigation = getCommercialSectionNavigation(area, current);
  if (!navigation) return null;
  const activeValue = navigation.links.some(link => link.key === current) ? current : false;

  if (navigation.groups) return <nav className={`${classes.root} ${classes.groupedRoot}`} aria-label={`${navigation.label} sections`}>
    <div className={`${classes.copy} ${classes.groupedCopy}`}>
      <Typography className={classes.label} variant="subtitle2">{navigation.label}</Typography>
      <Typography variant="caption" color="textSecondary">{navigation.description}</Typography>
    </div>
    <div className={classes.groupGrid}>
      {navigation.groups.map(group => <section className={classes.group} key={group.label} aria-label={group.label}>
          <Typography className={classes.groupLabel} variant="overline" color="textSecondary">{group.label}</Typography>
          <div className={classes.groupLinks}>
            {group.links.map(link => <a
              aria-current={link.key === current ? 'page' : undefined}
              className={`${classes.groupLink} ${link.key === current ? classes.activeGroupLink : ''}`}
              href={link.href}
              key={link.key}
            >{link.label}</a>)}
          </div>
        </section>)}
    </div>
  </nav>;

  return <nav className={classes.root} aria-label={`${navigation.label} sections`}>
    <div className={classes.copy}>
      <Typography className={classes.label} variant="subtitle2">{navigation.label}</Typography>
      <Typography variant="caption" color="textSecondary">{navigation.description}</Typography>
    </div>
    <Tabs
      className={classes.tabs}
      value={activeValue}
      variant="scrollable"
      scrollButtons="auto"
      aria-label={`${navigation.label} views`}
    >
      {navigation.links.map(link => <Tab
        component="a"
        href={link.href}
        key={link.key}
        label={link.label}
        value={link.key}
      />)}
    </Tabs>
  </nav>;
}

export default CommercialSectionNavigation;
